/**
 * Overall intent of this file:
 *
 * Defines various context interfaces and classes for logging purposes, allowing for structured and consistent logging across the application. These contexts can be used to provide additional information when logging events, such as user-related or map-related information.
 */

/**
 * The `AppLogContext` interface represents the overall context for logging, which can include specific contexts like `UserContext` and `MapContext`, as well as any other relevant information that may be useful for debugging or monitoring purposes.
 *
 * We use an interface here so that we can provide good intellisense for the most common contexts, but also allow for any other custom contexts to be added as needed without having to modify the base context structure.
 */
// NOTE: Remember to add any new contexts to this interface!
export interface AppLogContext {
  user?: UserLoggingContext;
  map?: MapLoggingContext;
  webview?: WebViewLoggingContext;

  [key: string]: unknown;
}

/**
 * Represents the base context for logging, which can be extended to include specific information relevant to different types of logs.
 *
 * This class serves as a foundation for creating more specialized contexts, such as user-related or map-related contexts, allowing for structured and consistent logging across the application.
 */
export abstract class LogContext {
  [key: string]: unknown;
}

/**
 * Represents the context for user-related logs, including the user ID and optionally the username. This can be used to provide additional information when logging events related to user actions, such as login attempts, profile updates, or errors encountered while performing user-related operations.
 */
export class UserLoggingContext extends LogContext {
  userId: string;
  username?: string;

  constructor({ userId, username }: { userId: string; username?: string }) {
    super();
    this.userId = userId;
    this.username = username;
  }
}

/**
 * Represents the context for map-related logs, including the map ID and optionally the map name. This can be used to provide additional information when logging events related to maps, such as loading a map, updating map settings, or encountering errors while working with maps.
 */
export class MapLoggingContext extends LogContext {
  mapId: string;
  mapName?: string;

  constructor({ mapId, mapName }: { mapId: string; mapName?: string }) {
    super();
    this.mapId = mapId;
    this.mapName = mapName;
  }
}

/**
 * Represents the context for web view-related logs, including the URL and HTTP method. This can be used to provide additional information when logging events related to web view interactions, such as making API requests, loading external content, or encountering errors while working with web views.
 */
export class WebViewLoggingContext extends LogContext {
  url?: string;
  method?: string;
  config?: string;

  constructor({ url, method, config }: { url?: string; method?: string; config?: string }) {
    super();
    this.url = url;
    this.method = method;
    this.config = config;
  }
}

/*
* In SentryAdapter we can then do something like:
*
* if (context instanceof UserContext) {
*   Sentry.setUser({ id: context.userId, username: context.username });
* ...
* }
 */
